import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import ResizableHeader from "@/components/ResizableHeader";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { NewsletterSignupBlock } from "@/components/uitripled/newsletter-signup-block-shadcnui";
import { blogPosts } from "@/data/blog-posts";

const Blog = () => {
  const navigate = useNavigate();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error || 'Failed to subscribe');
      }

      await response.json();
      
      toast({
        title: "Successfully subscribed!",
        description: "Thank you for subscribing to our newsletter. We'll keep you updated with the latest tips and insights.",
      });
      
      setEmail("");
    } catch (error) {
      toast({
        title: "Subscription failed",
        description: error instanceof Error ? error.message : "Failed to subscribe. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Blog | BankConvert</title>
        <meta name="description" content="Tips, tutorials, and insights for converting bank statements to Excel and managing financial data." />
        <link rel="canonical" href="https://www.bankconvert.org/blog" />
        <meta property="og:title" content="Blog | BankConvert" />
        <meta property="og:description" content="Tips, tutorials, and insights for converting bank statements to Excel and managing financial data." />
        <meta property="og:url" content="https://www.bankconvert.org/blog" />
      </Helmet>
      <ResizableHeader />
      <main>
        {/* Hero Section */}
        <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-subtle">
          <div className="container px-4 sm:px-6">
            <div className="text-center mb-8 sm:mb-12 md:mb-16 space-y-3 sm:space-y-4">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold px-2">
                Our Blog
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
                Tips, tutorials, and insights for better financial data management
              </p>
            </div>
          </div>
        </section>

        {/* Blog Posts */}
        <section className="py-8 sm:py-12 md:py-16">
          <div className="container px-4 sm:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-6xl mx-auto">
              {blogPosts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-medium transition-all duration-300 bg-card border-border hover:border-primary/50">
                  <div className="aspect-video bg-muted flex items-center justify-center">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                        {post.category}
                      </span>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>
                    
                    <h3 className="text-xl font-semibold line-clamp-2">
                      {post.title}
                    </h3>
                    
                    <p className="text-muted-foreground line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-end pt-4">
                      <Button 
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/blog/${post.slug || post.id}`)}
                        className="flex items-center gap-1 text-primary hover:text-primary/80 hover:bg-primary/10 transition-colors"
                      >
                        <span className="text-sm font-medium">Read More</span>
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 bg-muted/30">
          <div className="container px-4 sm:px-6 max-w-4xl mx-auto">
            <h2 className="text-lg font-semibold mb-4">Popular Guides</h2>
            <ul className="space-y-2">
              <li>
                <a href="/blog/convert-pdf-bank-statement-to-excel" className="text-primary hover:underline">
                  How to convert PDF bank statements to Excel
                </a>
              </li>
              <li>
                <a href="/blog/csv-to-excel-converter-free-instant-private" className="text-primary hover:underline">
                  Free private CSV to Excel converter — no upload required
                </a>
              </li>
              <li>
                <a href="/blog/announcing-paid-plans-bank-statement-pdf-converter" className="text-primary hover:underline">
                  BankConvert paid plans — what's included
                </a>
              </li>
              <li>
                <a href="/blog/convert-bank-statements-to-excel-30-seconds" className="text-primary hover:underline">
                  Convert bank statements to Excel in 30 seconds
                </a>
              </li>
              <li>
                <a href="/blog/bankconvert-vs-other-bank-statement-converter-tools" className="text-primary hover:underline">
                  BankConvert vs other bank statement converter tools
                </a>
              </li>
            </ul>
          </div>
        </section>

        <NewsletterSignupBlock />
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
